import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  addFeedPhoto,
  addFeedVideo,
  deactivateFeedModel,
  deactivateFeedPhoto,
  deactivateFeedVideo,
  deletePinnedFeedModel,
  getFeedModel,
  listFeedVideos,
  listPinnedFeedModels,
  upsertPinnedFeedModel
} from '../api/feed';
import { patchModel } from '../api/models';
import { uploadFile, uploadVideoFile } from '../api/files';
import type { AdminFeedModelVideoResponse, AdminFeedPinnedModel } from '../api/types';
import { pickImageUrl } from '../utils/imageUrls';
import PageHeader from '../components/shared/PageHeader';
import {
  ArrowLeft, RefreshCw, Edit3, Upload, AlertCircle, CheckCircle2, X, Plus, Video
} from 'lucide-react';

const FeedModelDetailPage: React.FC = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [metaMessage, setMetaMessage] = useState<string | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [activeError, setActiveError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [pinPosition, setPinPosition] = useState('');
  const [pinPhotoId, setPinPhotoId] = useState('');
  const [pinActive, setPinActive] = useState(true);
  const [pinMessage, setPinMessage] = useState<string | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  const [videoUploadError, setVideoUploadError] = useState<string | null>(null);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);

  const {
    data: model,
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ['feed', 'model', username],
    queryFn: () => getFeedModel(username ?? ''),
    enabled: Boolean(username),
    retry: false
  });

  const {
    data: pinnedModels,
    isLoading: isPinnedLoading,
    isError: isPinnedError
  } = useQuery({
    queryKey: ['feed', 'pinned'],
    queryFn: listPinnedFeedModels
  });

  const {
    data: videos,
    isLoading: isVideosLoading,
    refetch: refetchVideos
  } = useQuery({
    queryKey: ['feed', 'model', username, 'videos'],
    queryFn: () => listFeedVideos(username ?? ''),
    enabled: Boolean(username)
  });

  useEffect(() => {
    if (model) {
      setDescription(model.description ?? '');
      setPrice(model.price ?? '');
    }
  }, [model]);

  const pinnedEntry = useMemo<AdminFeedPinnedModel | null>(() => {
    if (!pinnedModels || !model) {
      return null;
    }
    return pinnedModels.find((entry) => entry.modelUsername === model.modelUsername) ?? null;
  }, [pinnedModels, model]);

  useEffect(() => {
    if (!model) {
      return;
    }

    if (pinnedEntry) {
      setPinPosition(
        typeof pinnedEntry.position === 'number' && !Number.isNaN(pinnedEntry.position)
          ? String(pinnedEntry.position)
          : ''
      );
      setPinPhotoId(
        typeof pinnedEntry.photoId === 'number' && !Number.isNaN(pinnedEntry.photoId)
          ? String(pinnedEntry.photoId)
          : ''
      );
      setPinActive(Boolean(pinnedEntry.active));
    } else {
      setPinPosition('');
      const candidate = model.photos.find((photo) => photo.active) ?? model.photos[0];
      setPinPhotoId(candidate ? String(candidate.id) : '');
      setPinActive(true);
    }

    setPinMessage(null);
    setPinError(null);
  }, [model, pinnedEntry]);

  const selectedPinPhoto = useMemo(() => {
    if (!model) {
      return undefined;
    }
    return model.photos.find((photo) => String(photo.id) === pinPhotoId);
  }, [model, pinPhotoId]);

  const pinPreviewUrl = useMemo(() => {
    if (!selectedPinPhoto) {
      return null;
    }
    return pickImageUrl(selectedPinPhoto.imageUrls, selectedPinPhoto.fileName);
  }, [selectedPinPhoto]);

  const isActive = useMemo(() => model?.photos.some((photo) => photo.active) ?? false, [model]);

  const updateMetaMutation = useMutation({
    mutationFn: async () => {
      if (!model) {
        throw new Error('Model is not loaded');
      }
      const payload: { description?: string; price?: string } = {};
      payload.description = description;
      payload.price = price;
      await patchModel(model.id, payload);
    },
    onSuccess: async () => {
      setMetaMessage('Metadata updated successfully.');
      setMetaError(null);
      await queryClient.invalidateQueries({ queryKey: ['feed', 'models'] });
      await refetch();
    },
    onError: (error: unknown) => {
      console.error('Failed to update metadata', error);
      setMetaMessage(null);
      setMetaError('Failed to update metadata. Please try again.');
    }
  });

  const setActiveMutation = useMutation({
    mutationFn: async (nextActive: boolean) => {
      if (!model) {
        throw new Error('Model is not loaded');
      }

      if (!nextActive) {
        await deactivateFeedModel(model.modelUsername, model.imageFilename);
        return;
      }

      const candidate = model.photos.find((photo) => photo.active) ?? model.photos[0];
      if (!candidate) {
        throw new Error('No photos available to activate. Please add a photo first.');
      }
      await addFeedPhoto(model.modelUsername, candidate.fileName);
    },
    onSuccess: async () => {
      setActiveError(null);
      await refetch();
    },
    onError: (error: unknown) => {
      console.error('Failed to toggle activity state', error);
      setActiveError(error instanceof Error ? error.message : 'Failed to update activity state.');
    }
  });

  const deactivatePhotoMutation = useMutation({
    mutationFn: async (fileName: string) => {
      if (!model) {
        throw new Error('Model is not loaded');
      }
      return deactivateFeedPhoto(model.modelUsername, fileName);
    },
    onSuccess: async () => {
      await refetch();
    },
    onError: (error: unknown) => {
      console.error('Failed to deactivate photo', error);
      setUploadError('Failed to deactivate the photo. Please try again.');
    }
  });

  const addPhotoMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!model) {
        throw new Error('Model is not loaded');
      }

      const extension = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
      const uuid = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2);
      const desiredName = `${model.modelUsername}_swiper_${uuid}.${extension}`;

      const response = await uploadFile(file, desiredName);
      const storedName = Object.values(response)[0];
      if (typeof storedName !== 'string' || !storedName) {
        throw new Error('Upload did not return a filename.');
      }
      await addFeedPhoto(model.modelUsername, storedName);
    },
    onMutate: () => {
      setIsUploading(true);
      setUploadError(null);
    },
    onSuccess: async () => {
      setIsUploading(false);
      setUploadError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await refetch();
    },
    onError: (error: unknown) => {
      console.error('Failed to add swiper photo', error);
      setIsUploading(false);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload photo.');
    }
  });

  const addVideoMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!model) {
        throw new Error('Model is not loaded');
      }

      const extension = file.name.split('.').pop()?.toLowerCase() ?? 'mp4';
      const uuid = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2);
      const desiredName = `${model.modelUsername}_swiper_video_${uuid}.${extension}`;

      const response = await uploadVideoFile(file, desiredName);
      const storedName = Object.values(response)[0];
      if (typeof storedName !== 'string' || !storedName) {
        throw new Error('Upload did not return a filename.');
      }
      await addFeedVideo(model.modelUsername, { fileName: storedName });
    },
    onMutate: () => {
      setIsUploadingVideo(true);
      setVideoUploadError(null);
    },
    onSuccess: async () => {
      setIsUploadingVideo(false);
      setVideoUploadError(null);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
      await refetchVideos();
    },
    onError: (error: unknown) => {
      console.error('Failed to add swiper video', error);
      setIsUploadingVideo(false);
      setVideoUploadError(error instanceof Error ? error.message : 'Failed to upload video.');
    }
  });

  const deactivateVideoMutation = useMutation({
    mutationFn: async (fileName: string) => {
      if (!model) {
        throw new Error('Model is not loaded');
      }
      return deactivateFeedVideo(model.modelUsername, fileName);
    },
    onSuccess: async () => {
      await refetchVideos();
    },
    onError: (error: unknown) => {
      console.error('Failed to deactivate video', error);
      setVideoUploadError('Failed to deactivate the video. Please try again.');
    }
  });

  const upsertPinnedMutation = useMutation({
    mutationFn: async () => {
      if (!model) {
        throw new Error('Model is not loaded');
      }

      if (!pinPhotoId || pinPhotoId.trim().length === 0) {
        throw new Error('Please select a photo for the swiper pin.');
      }

      const parsedPhotoId = Number.parseInt(pinPhotoId, 10);
      if (!Number.isFinite(parsedPhotoId) || Number.isNaN(parsedPhotoId) || parsedPhotoId <= 0) {
        throw new Error('Please select a valid photo for the swiper pin.');
      }

      if (!pinPosition || pinPosition.trim().length === 0) {
        throw new Error('Please provide a position for the swiper pin.');
      }

      const parsedPosition = Number.parseInt(pinPosition, 10);
      if (!Number.isFinite(parsedPosition) || Number.isNaN(parsedPosition)) {
        throw new Error('Position must be a number.');
      }

      if (!Number.isInteger(parsedPosition) || parsedPosition < 0) {
        throw new Error('Position must be zero or a positive whole number.');
      }

      return upsertPinnedFeedModel(model.modelUsername, {
        position: parsedPosition,
        photoId: parsedPhotoId,
        active: pinActive
      });
    },
    onMutate: () => {
      setPinMessage(null);
      setPinError(null);
    },
    onSuccess: async (response) => {
      setPinMessage('Swiper pin saved successfully.');
      setPinError(null);
      setPinPosition(response.position != null ? String(response.position) : '');
      setPinPhotoId(response.photoId != null ? String(response.photoId) : '');
      setPinActive(Boolean(response.active));
      await queryClient.invalidateQueries({ queryKey: ['feed', 'pinned'] });
    },
    onError: (error: unknown) => {
      console.error('Failed to save swiper pin', error);
      setPinMessage(null);
      setPinError(error instanceof Error ? error.message : 'Failed to save swiper pin. Please try again.');
    }
  });

  const deletePinnedMutation = useMutation({
    mutationFn: async () => {
      if (!model) {
        throw new Error('Model is not loaded');
      }
      await deletePinnedFeedModel(model.modelUsername);
    },
    onMutate: () => {
      setPinMessage(null);
      setPinError(null);
    },
    onSuccess: async () => {
      setPinMessage('Swiper pin removed.');
      setPinError(null);
      await queryClient.invalidateQueries({ queryKey: ['feed', 'pinned'] });
    },
    onError: (error: unknown) => {
      console.error('Failed to remove swiper pin', error);
      setPinMessage(null);
      setPinError(error instanceof Error ? error.message : 'Failed to remove swiper pin. Please try again.');
    }
  });

  const isPinActionPending = upsertPinnedMutation.isPending || deletePinnedMutation.isPending;

  const handlePhotoInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('Image must be smaller than 10MB.');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file.');
      return;
    }

    addPhotoMutation.mutate(file);
  };

  const handleVideoInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      setVideoUploadError('Video must be smaller than 100MB.');
      return;
    }

    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      setVideoUploadError('Please select a video file (MP4, WebM, or MOV).');
      return;
    }

    addVideoMutation.mutate(file);
  };

  if (isLoading) {
    return (
      <>
        <PageHeader title="Swiper model" />
        <div className="hf-content">
          <div className="hf-card"><div className="hf-card-body" style={{ textAlign: 'center', color: 'var(--fg-3)' }}>Loading swiper model…</div></div>
        </div>
      </>
    );
  }

  if (isError || !model) {
    return (
      <>
        <PageHeader title="Swiper model" />
        <div className="hf-content">
          <div className="hf-banner danger">
            <AlertCircle size={14} /> Unable to load the swiper model.
          </div>
          <button className="hf-btn" onClick={() => navigate('/feed')}>
            <ArrowLeft size={14} /> Back to swiper
          </button>
        </div>
      </>
    );
  }

  const hasPhotos = model.photos.length > 0;
  const activePhotos = model.photos.filter(p => p.active);
  const inactivePhotos = model.photos.filter(p => !p.active);
  const heroImageUrl = pickImageUrl(model.imageUrls, model.imageFilename);
  const initials = (model.modelName || model.modelUsername || '?').charAt(0).toUpperCase();

  const headerTitle = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
      <button className="hf-btn icon ghost" onClick={() => navigate('/feed')} aria-label="Back">
        <ArrowLeft size={16} />
      </button>
      <span className="hf-avatar lg" style={{ overflow: 'hidden' }}>
        {heroImageUrl
          ? <img src={heroImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          : initials}
      </span>
      <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
        {model.modelName || model.modelUsername}
      </span>
      <span className={`hf-tag ${isActive ? 'success' : 'danger'}`}>
        {isActive ? 'active' : 'inactive'}
      </span>
    </span>
  );

  const headerSubtitle = (
    <>
      <span className="hf-mono">@{model.modelUsername}</span>
      {' · '}<span className="hf-mono">#{model.id}</span>
      {' · '}{activePhotos.length} active photos
      {videos && videos.length > 0 && <> · {videos.length} videos</>}
    </>
  );

  const headerActions = (
    <>
      <button className="hf-btn" onClick={() => refetch()} disabled={updateMetaMutation.isPending}>
        <RefreshCw size={14} /> Re-sync
      </button>
      <Link to={`/models/${model.id}/edit`} className="hf-btn primary">
        <Edit3 size={14} /> Edit
      </Link>
    </>
  );

  return (
    <>
      <PageHeader title={headerTitle} subtitle={headerSubtitle} actions={headerActions} />
      <div className="hf-content">


      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <div>
            <h2 className="card-title">Metadata</h2>
            <p className="card-subtitle">Update swiper metadata such as description, price and status.</p>
          </div>
          <span className={`badge ${isActive ? 'badge-success' : 'badge-danger'}`}>
            {isActive ? 'Active' : 'Non-active'}
          </span>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            updateMetaMutation.mutate();
          }}
        >
          <div style={{ display: 'grid', gap: '1.25rem', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            <div>
              <label htmlFor="feed-description" className="form-label">
                Description
              </label>
              <textarea
                id="feed-description"
                className="form-input"
                style={{ minHeight: '140px', resize: 'vertical' }}
                placeholder="Enter description..."
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor="feed-price" className="form-label">
                Price
              </label>
              <input
                id="feed-price"
                className="form-input"
                type="text"
                placeholder="Enter price..."
                value={price}
                onChange={(event) => setPrice(event.target.value)}
              />

              <div style={{ marginTop: '1.5rem' }}>
                <label className="form-label" htmlFor="swiper-active-toggle">
                  Swiper status
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span className={`badge ${isActive ? 'badge-success' : 'badge-danger'}`}>
                    {isActive ? 'Active' : 'Non-active'}
                  </span>
                  <button
                    type="button"
                    id="swiper-active-toggle"
                    className="btn btn-secondary"
                    onClick={() => setActiveMutation.mutate(!isActive)}
                    disabled={setActiveMutation.isPending}
                  >
                    {setActiveMutation.isPending ? 'Updating...' : isActive ? 'Mark as non-active' : 'Mark as active'}
                  </button>
                </div>
                {activeError && (
                  <p style={{ color: 'var(--color-danger)', marginTop: '0.5rem' }}>{activeError}</p>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem', gap: '0.75rem' }}>
            <button className="btn btn-primary" type="submit" disabled={updateMetaMutation.isPending}>
              {updateMetaMutation.isPending ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
        {metaMessage && (
          <div className="notification notification-success" role="status" style={{ marginTop: '1rem' }}>
            <span>✅</span>
            <div>{metaMessage}</div>
          </div>
        )}
        {metaError && (
          <div className="notification notification-error" role="alert" style={{ marginTop: '1rem' }}>
            <span>❌</span>
            <div>{metaError}</div>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <div>
            <h2 className="card-title">Swiper pin</h2>
            <p className="card-subtitle">Pin this model to the swiper first screen and manage its visibility.</p>
          </div>
          {isPinnedLoading ? (
            <span className="badge badge-gray">Loading…</span>
          ) : pinnedEntry ? (
            <span className={`badge ${pinnedEntry.active ? 'badge-success' : 'badge-gray'}`}>
              {pinnedEntry.active ? 'Pinned' : 'Inactive'} · position{' '}
              {typeof pinnedEntry.position === 'number' ? pinnedEntry.position : '—'}
            </span>
          ) : (
            <span className="badge badge-gray">Not pinned</span>
          )}
        </div>

        {isPinnedLoading ? (
          <div style={{ textAlign: 'center', padding: '1.25rem 0' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 0.75rem' }} />
            <p style={{ color: 'var(--color-gray-600)', margin: 0 }}>Loading swiper pin...</p>
          </div>
        ) : isPinnedError ? (
          <div className="notification notification-error" role="alert">
            <span>⚠️</span>
            <div>Unable to load swiper pin data. Please refresh the page.</div>
          </div>
        ) : (
          <>
            {!hasPhotos && (
              <div className="notification notification-warning" role="alert" style={{ marginBottom: '1rem' }}>
                <span>ℹ️</span>
                <div>Add at least one photo before pinning this model to the swiper.</div>
              </div>
            )}
            <form
              onSubmit={(event) => {
                event.preventDefault();
                upsertPinnedMutation.mutate();
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gap: '1.25rem',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))'
                }}
              >
                <div>
                  <label htmlFor="swiper-pin-photo" className="form-label">
                    Swiper photo
                  </label>
                  <select
                    id="swiper-pin-photo"
                    className="form-input"
                    value={pinPhotoId}
                    onChange={(event) => setPinPhotoId(event.target.value)}
                    disabled={!hasPhotos || isPinActionPending}
                  >
                    <option value="">Select a photo...</option>
                    {model.photos.map((photo) => (
                      <option key={photo.id} value={photo.id}>
                        #{photo.id} — {photo.fileName}
                        {photo.active ? ' (active)' : ''}
                      </option>
                    ))}
                  </select>
                  {!selectedPinPhoto && pinPhotoId && (
                    <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--color-danger)' }}>
                      Selected pinned photo is not available. Choose another photo to update the swiper pin.
                    </p>
                  )}
                  {selectedPinPhoto && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <div
                        style={{
                          borderRadius: 'var(--radius-md)',
                          overflow: 'hidden',
                          background: 'var(--color-gray-100)',
                          height: '160px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {pinPreviewUrl ? (
                          <img
                            src={pinPreviewUrl}
                            alt={selectedPinPhoto.fileName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(event) => {
                              event.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              color: 'var(--color-gray-500)',
                              fontSize: '0.875rem'
                            }}
                          >
                            No preview
                          </div>
                        )}
                      </div>
                      <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>
                        {selectedPinPhoto.active
                          ? 'Photo is currently active in swiper.'
                          : 'Photo is currently marked as non-active.'}
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <label htmlFor="swiper-pin-position" className="form-label">
                    Swiper position
                  </label>
                  <input
                    id="swiper-pin-position"
                    className="form-input"
                    type="number"
                    min={0}
                    step={1}
                    value={pinPosition}
                    onChange={(event) => setPinPosition(event.target.value)}
                    placeholder="Enter swiper position..."
                    disabled={!hasPhotos || isPinActionPending}
                  />
                  <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>
                    Lower numbers appear earlier on the swiper carousel.
                  </p>
                  <div style={{ marginTop: '1rem' }}>
                    <label
                      htmlFor="swiper-pin-active"
                      className="form-label"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
                    >
                      <input
                        id="swiper-pin-active"
                        type="checkbox"
                        checked={pinActive}
                        onChange={(event) => setPinActive(event.target.checked)}
                        disabled={!hasPhotos || isPinActionPending}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <span>Show this pin on the swiper</span>
                    </label>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={!hasPhotos || isPinActionPending}
                >
                  {upsertPinnedMutation.isPending ? 'Saving...' : 'Save swiper pin'}
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => deletePinnedMutation.mutate()}
                  disabled={isPinActionPending || !pinnedEntry}
                >
                  {deletePinnedMutation.isPending ? 'Removing...' : 'Remove pin'}
                </button>
              </div>
            </form>
            {pinMessage && (
              <div className="notification notification-success" role="status" style={{ marginTop: '1rem' }}>
                <span>✅</span>
                <div>{pinMessage}</div>
              </div>
            )}
            {pinError && (
              <div className="notification notification-error" role="alert" style={{ marginTop: '1rem' }}>
                <span>⚠️</span>
                <div>{pinError}</div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Photos</h2>
            <p className="card-subtitle">Manage photos associated with the swiper model.</p>
          </div>
          <div>
            <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()} disabled={isUploading || addPhotoMutation.isPending}>
              {isUploading || addPhotoMutation.isPending ? 'Uploading...' : 'Add photo'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoInput}
              style={{ display: 'none' }}
            />
          </div>
        </div>
        {uploadError && (
          <div className="notification notification-error" role="alert" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span>
            <div>{uploadError}</div>
          </div>
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '1rem'
          }}
        >
          {model.photos.map((photo) => {
            const photoUrl = pickImageUrl(photo.imageUrls, photo.fileName);
            return (
              <div key={photo.id} className="card card-compact" style={{ padding: '0.75rem' }}>
                <div
                  style={{
                    position: 'relative',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    background: 'var(--color-gray-100)',
                    marginBottom: '0.75rem',
                    height: '180px'
                  }}
                >
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={photo.fileName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(event) => {
                        event.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: 'var(--color-gray-500)',
                        fontSize: '0.875rem'
                      }}
                    >
                      No preview
                    </div>
                  )}
                  <div style={{ position: 'absolute', top: '0.75rem', left: '0.75rem' }}>
                    <span className={`badge ${photo.active ? 'badge-success' : 'badge-danger'}`}>
                      {photo.active ? 'Active' : 'Non-active'}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', wordBreak: 'break-all', marginBottom: '0.5rem' }}>
                  {photo.fileName}
                </div>
                <button
                  className="btn btn-secondary"
                  style={{ width: '100%' }}
                  onClick={() => deactivatePhotoMutation.mutate(photo.fileName)}
                  disabled={deactivatePhotoMutation.isPending || !photo.active}
                >
                  {deactivatePhotoMutation.isPending ? 'Processing...' : photo.active ? 'Mark as non-active' : 'Already non-active'}
                </button>
              </div>
            );
          })}
          {model.photos.length === 0 && (
            <div className="card" style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
              <p style={{ color: 'var(--color-gray-600)', margin: 0 }}>This model does not have any photos yet.</p>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <div>
            <h2 className="card-title">Videos</h2>
            <p className="card-subtitle">Manage videos associated with the swiper model.</p>
          </div>
          <div>
            <button
              className="btn btn-primary"
              onClick={() => videoInputRef.current?.click()}
              disabled={isUploadingVideo || addVideoMutation.isPending}
            >
              {isUploadingVideo || addVideoMutation.isPending ? 'Uploading...' : 'Add video'}
            </button>
            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={handleVideoInput}
              style={{ display: 'none' }}
            />
          </div>
        </div>
        {videoUploadError && (
          <div className="notification notification-error" role="alert" style={{ marginBottom: '1rem' }}>
            <span>Warning</span>
            <div>{videoUploadError}</div>
          </div>
        )}
        {isVideosLoading ? (
          <div style={{ textAlign: 'center', padding: '1.25rem 0' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 0.75rem' }} />
            <p style={{ color: 'var(--color-gray-600)', margin: 0 }}>Loading videos...</p>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem'
            }}
          >
            {videos?.map((video: AdminFeedModelVideoResponse) => {
              const thumbnailUrl = video.thumbnailUrls ? pickImageUrl(video.thumbnailUrls, video.thumbnailFileName ?? '') : null;
              return (
                <div key={video.id} className="card card-compact" style={{ padding: '0.75rem' }}>
                  <div
                    style={{
                      position: 'relative',
                      borderRadius: 'var(--radius-md)',
                      overflow: 'hidden',
                      background: 'var(--color-gray-100)',
                      marginBottom: '0.75rem',
                      height: '180px'
                    }}
                  >
                    {video.videoUrl ? (
                      <video
                        src={video.videoUrl}
                        poster={thumbnailUrl ?? undefined}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        muted
                        playsInline
                        preload="metadata"
                        onMouseEnter={(e) => e.currentTarget.play()}
                        onMouseLeave={(e) => {
                          e.currentTarget.pause();
                          e.currentTarget.currentTime = 0;
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          color: 'var(--color-gray-500)',
                          fontSize: '0.875rem'
                        }}
                      >
                        No preview
                      </div>
                    )}
                    <div style={{ position: 'absolute', top: '0.75rem', left: '0.75rem' }}>
                      <span className={`badge ${video.active ? 'badge-success' : 'badge-danger'}`}>
                        {video.active ? 'Active' : 'Non-active'}
                      </span>
                    </div>
                    {video.durationSeconds && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '0.5rem',
                          right: '0.5rem',
                          background: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem'
                        }}
                      >
                        {Math.floor(video.durationSeconds / 60)}:{String(video.durationSeconds % 60).padStart(2, '0')}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', wordBreak: 'break-all', marginBottom: '0.5rem' }}>
                    {video.fileName}
                  </div>
                  <button
                    className="btn btn-secondary"
                    style={{ width: '100%' }}
                    onClick={() => deactivateVideoMutation.mutate(video.fileName)}
                    disabled={deactivateVideoMutation.isPending || !video.active}
                  >
                    {deactivateVideoMutation.isPending ? 'Processing...' : video.active ? 'Mark as non-active' : 'Already non-active'}
                  </button>
                </div>
              );
            })}
            {(!videos || videos.length === 0) && (
              <div className="hf-card" style={{ gridColumn: '1 / -1' }}>
                <div className="hf-card-body" style={{ textAlign: 'center', color: 'var(--fg-3)' }}>This model does not have any videos yet.</div>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default FeedModelDetailPage;
